import { create } from "zustand";
import type { StoreApi, UseBoundStore } from "zustand";
import { scopedIdbGet as idbGet, scopedIdbSet as idbSet } from "@/lib/client-scoped-idb";
import { toast } from "sonner";
import type { Workshop } from "@/content/workshops";
import { isWorkshopBffEnabled } from "@/env";
import {
  deleteDoc,
  DocsConflictError,
  getJsonWithEtag,
  listDocs,
  putJsonWithEtag,
} from "@/lib/docs-api";

/**
 * Factory for the "workshop record" stores — per-workshop logs of
 * facilitator-captured records (decisions, actions, assumptions,
 * transcripts). These four shared ~90% byte-identical bodies
 * (`emptyEntries`/`persist`/`hydrate`/`add`/`update`/`remove` plus the
 * sanitise loop); the only real per-store differences are the IDB key, the
 * record type, and the per-item field validation. This collapses that
 * duplication to a single implementation.
 *
 * Durability has two backends, chosen by `VITE_WORKSHOP_BACKEND`
 * (see `isWorkshopBffEnabled`):
 *   - "idb" (default): IndexedDB only — the R1 offline posture (no sign-in,
 *     survives reload). This is what seed-mode smoke tests use.
 *   - "bff": each record is a JSON blob on the document-storage BFF at
 *     `workshops/<wsId>/records/<type>/<id>.json` (one blob per record, so
 *     different records never contend; see the "workshop records as JSON on the
 *     blob plane" ADR). IndexedDB stays as an optimistic + offline read cache:
 *     writes go local-first then fire-and-forget to the BFF; hydrate prefers the
 *     BFF and falls back to the cache when it's unreachable.
 *
 * Same-record concurrency (E1): two facilitators editing the *same* record do
 * contend. Each pull captures the blob's ETag ({@link recordEtags}); a write
 * sends it as `If-Match` (or `If-None-Match: *` for a never-seen record), so a
 * racing overwrite fails with 412 instead of silently clobbering. Because a
 * record is an opaque unit (no field-level merge), on 412 we re-pull, adopt the
 * winning copy into memory + cache, and toast the user to re-apply their edit —
 * rather than guessing a merge.
 *
 * The public store API is identical in both modes, so the ~8 consumers and
 * the store tests don't care which backend is live.
 */

/** The BFF tier per-workshop content is stored under. */
const WORKSHOPS_TIER = "workshops";

/** Mirrors `SyncState` in `@/stores/synced-stores`. Duplicated (not imported)
 *  to avoid an import cycle: `synced-stores` → `useWorkshopOverrides` → this
 *  factory. The two unions are structurally identical, so a factory store is
 *  assignable to `SyncableStore` when registered. */
type SyncState = "idle" | "syncing" | "synced" | "error" | "disabled";

/** Every persisted record is scoped to a workshop and uniquely keyed. */
export type WorkshopRecord = { id: string; workshopId: string };

export type EntriesByWorkshop<T> = Record<Workshop["id"], T[]>;

export type WorkshopRecordStore<T, P> = {
  /** The `<type>` path segment under the BFF `workshops` tier
   *  (`<wsId>/records/<recordType>/<id>.json`). Exposed so the E2 register-sync
   *  coordinator can route a changed-blob path to the owning store. Empty
   *  string when the store has no BFF backend configured. */
  recordType: string;
  entriesByWorkshop: EntriesByWorkshop<T>;
  isHydrated: boolean;
  /** Set when a BFF hydrate couldn't reach the records service at all (the
   *  cached copy is still shown). `null` in IDB-only mode and on success. */
  loadError: string | null;
  /**
   * Sync slice (the `SyncableSlice` shape `synced-stores.ts` expects).
   * Populated for every factory store, but only meaningful once a store is
   * registered in `SYNCED_STORES` — `sync()` force re-pulls from the BFF and
   * merges (vs `hydrate`, which is one-shot). Unregistered stores expose the
   * fields inertly (`syncState` tracks BFF availability; `sync()` still works
   * if called manually).
   */
  syncState: SyncState;
  lastSyncedAt: number | null;
  syncError: string | null;
  sync: () => Promise<void>;
  hydrate: () => Promise<void>;
  add: (workshopId: Workshop["id"], record: T) => void;
  /** Patch-merge a record by id. Transcripts don't surface this in their
   *  UI, but it's a harmless no-cost inheritance from the shared shape. */
  update: (workshopId: Workshop["id"], id: string, patch: P) => void;
  remove: (workshopId: Workshop["id"], id: string) => void;
  /**
   * One-time cutover migration: PUT every record in the durable IDB cache to
   * the BFF (idempotent — `id`-keyed) and return how many were pushed. No-op
   * (returns 0) when the BFF backend is disabled. Used by the unified area's
   * "upload my local data" action after the flag flips.
   */
  pushLocalToBff: () => Promise<number>;
  /**
   * E2 changed-since ingestion. Given blob paths the register-sync coordinator
   * observed as changed (from one cheap tier-wide `list?since=`), GET just the
   * ones belonging to THIS store's `recordType`, capture their etags, and merge
   * them into memory + cache (BFF wins on id; same `mergeOnSync` as `sync()`).
   * A no-op for paths of other types or when the BFF backend is off. Returns
   * how many of this store's records were ingested. Never throws — a single
   * unreadable record is skipped, a total failure leaves state untouched.
   *
   * This is what lets the 7 register stores go 15s-live without each re-listing
   * all 8 workshop buckets every tick (the cost that kept them off the poll).
   */
  ingestRemoteChanges: (changedPaths: string[]) => Promise<number>;
};

// The eight workshop buckets. Hardcoded (not derived from content) to keep
// behaviour byte-identical to the originals this replaced.
const WORKSHOP_IDS: Array<Workshop["id"]> = [
  "WS1",
  "WS2",
  "WS3",
  "WS4",
  "WS5",
  "WS6",
  "WS7",
  "WS8",
];

const emptyEntries = <T>(): EntriesByWorkshop<T> => {
  const out = {} as EntriesByWorkshop<T>;
  for (const id of WORKSHOP_IDS) out[id] = [];
  return out;
};

type Options<T> = {
  /** idb-keyval key the whole `entriesByWorkshop` map persists under. */
  idbKey: string;
  /**
   * Record type — the `<type>` path segment under the BFF `workshops` tier
   * (`<wsId>/records/<type>/<id>.json`). Required to enable the BFF backend;
   * a store without it stays IDB-only regardless of the flag.
   */
  recordType: string;
  /**
   * Validate + normalise one raw persisted record. Return `null` to drop
   * it (same best-effort threat model as the originals: anything we can't
   * recognise is discarded). `fallbackWorkshopId` is the bucket the record
   * was found under — use it when the record's own `workshopId` is missing.
   */
  sanitiseItem: (
    raw: Record<string, unknown>,
    fallbackWorkshopId: Workshop["id"],
  ) => T | null;
  /**
   * Custom merge for `sync()` re-pulls: combine the freshly-pulled BFF
   * snapshot (`fresh`) with the in-memory state (`local`). Defaults to
   * {@link unionById} (BFF wins on id conflicts — right for append-style
   * logs). A store whose records are mutated in place (e.g. the
   * singleton-per-workshop overrides) passes a recency-aware merge so a
   * just-saved local edit isn't clobbered by a stale poll.
   */
  mergeOnSync?: (
    fresh: EntriesByWorkshop<T>,
    local: EntriesByWorkshop<T>,
  ) => EntriesByWorkshop<T>;
};

export function createWorkshopRecordStore<
  T extends WorkshopRecord,
  P = Partial<T>,
>(opts: Options<T>): UseBoundStore<StoreApi<WorkshopRecordStore<T, P>>> {
  const { idbKey, recordType, sanitiseItem, mergeOnSync } = opts;

  /** BFF blob path for one record. `workshopId` is a plain `string` (not the
   *  narrow `Workshop["id"]` union): it's only a path segment, and a record's
   *  own `workshopId` field is typed `string`. The narrow-typed callers pass
   *  fine since `Workshop["id"]` is assignable to `string`. */
  const recordPath = (workshopId: string, id: string): string =>
    `${workshopId}/records/${recordType}/${id}.json`;

  /**
   * Parse a blob path `<wsId>/records/<type>/<id>.json` back to its parts,
   * returning the workshop bucket only when the path belongs to THIS store's
   * `recordType` and names a real workshop (E2 routing). `null` otherwise — so
   * a changed-path list mixing every register type is filtered to ours.
   */
  const matchRecordPath = (
    path: string,
  ): { wsId: Workshop["id"] } | null => {
    const m = /^([^/]+)\/records\/([^/]+)\/[^/]+\.json$/.exec(path);
    if (!m) return null;
    const [, wsId, type] = m;
    if (type !== recordType) return null;
    if (!WORKSHOP_IDS.includes(wsId as Workshop["id"])) return null;
    return { wsId: wsId as Workshop["id"] };
  };

  /**
   * Last-seen ETag per record blob path (E1). Populated on every pull, consumed
   * as the next write's `If-Match`. A path absent from the map means "never
   * pulled this session" → the write is create-only (`If-None-Match: *`). Scoped
   * to this factory instance (one store), keyed by the full blob path.
   */
  const recordEtags = new Map<string, string | null>();

  /** Whether to route durability through the BFF (vs IDB-only). */
  const bffEnabled = (): boolean => isWorkshopBffEnabled();

  const sanitise = (raw: unknown): EntriesByWorkshop<T> => {
    const out = emptyEntries<T>();
    if (typeof raw !== "object" || raw === null) return out;
    const obj = raw as Record<string, unknown>;
    for (const id of WORKSHOP_IDS) {
      const list = obj[id];
      if (!Array.isArray(list)) continue;
      const cleaned: T[] = [];
      for (const item of list) {
        if (typeof item !== "object" || item === null) continue;
        const rec = sanitiseItem(item as Record<string, unknown>, id);
        if (rec) cleaned.push(rec);
      }
      out[id] = cleaned;
    }
    return out;
  };

  /** Whole-map write to the IndexedDB cache. Best-effort (see jsdoc). */
  const cacheToIdb = async (entries: EntriesByWorkshop<T>): Promise<void> => {
    try {
      await idbSet(idbKey, entries);
    } catch {
      /* idb-keyval write failed — best-effort durability */
    }
  };

  /**
   * Load every record for this type from the BFF (one list + fan-out GET per
   * workshop). Resilient per workshop: a transient list failure for one
   * workshop falls back to that workshop's `fallback` bucket (the IDB cache)
   * rather than discarding the other seven's freshly-fetched data. Individual
   * unreadable/corrupt records are skipped, not fatal. Never throws — a total
   * BFF outage simply yields the full fallback (the cache).
   */
  const loadAllFromBff = async (
    fallback: EntriesByWorkshop<T>,
  ): Promise<{ entries: EntriesByWorkshop<T>; failures: number }> => {
    const out = emptyEntries<T>();
    let failures = 0;
    await Promise.all(
      WORKSHOP_IDS.map(async (wsId) => {
        try {
          const prefix = `${wsId}/records/${recordType}/`;
          const blobs = await listDocs(WORKSHOPS_TIER, prefix);
          const raws = await Promise.all(
            blobs.map(async (b) => {
              try {
                const { value, etag } = await getJsonWithEtag<
                  Record<string, unknown>
                >(WORKSHOPS_TIER, b.path);
                // Capture the etag so the next write to this record can send
                // If-Match. A list already carries it, but re-reading here keeps
                // the source single (the authoritative GET) and handles the
                // never-listed path too.
                recordEtags.set(b.path, etag);
                return value;
              } catch {
                return null; // skip a single corrupt/unreadable record
              }
            }),
          );
          // Await the raw (concrete-typed) JSON first, then map `sanitiseItem`
          // synchronously. Calling `sanitiseItem` inside the `Promise.all`
          // callback would push the generic `T` through `Awaited<…>`, widening
          // each element to `Awaited<T> | null` (TS can't prove `Awaited<T>` is
          // `T` for a bare type parameter) and breaking the `r is T` guard.
          out[wsId] = raws
            .map((raw) => (raw ? sanitiseItem(raw, wsId) : null))
            .filter((r): r is T => r !== null);
        } catch {
          // This workshop's list failed — keep its cached bucket so one
          // flaky prefix doesn't blank the whole area.
          failures += 1;
          out[wsId] = fallback[wsId] ?? [];
        }
      }),
    );
    return { entries: out, failures };
  };

  /**
   * Union two bucket maps by record id, `primary` winning on conflicts:
   * `primary ∪ {records in secondary whose id isn't in primary}`.
   *
   * Used on hydrate so the BFF snapshot (authoritative) is merged with local
   * records the BFF doesn't have yet — both optimistic in-flight writes AND
   * records captured while the backend was IDB-only (the cutover case). This
   * is what keeps offline-captured data visible after the flag flips and stops
   * an empty BFF snapshot from wiping it (it stays until migrated). The only
   * cost is the documented R1 cross-device edge: a record deleted on another
   * device lingers in this browser's cache (single-device deletes update the
   * cache, so they don't resurrect).
   */
  const unionById = (
    primary: EntriesByWorkshop<T>,
    secondary: EntriesByWorkshop<T>,
  ): EntriesByWorkshop<T> => {
    const out = emptyEntries<T>();
    for (const id of WORKSHOP_IDS) {
      const primaryBucket = primary[id] ?? [];
      const seen = new Set(primaryBucket.map((r) => r.id));
      const extra = (secondary[id] ?? []).filter((r) => !seen.has(r.id));
      out[id] = [...primaryBucket, ...extra];
    }
    return out;
  };

  /** Merge strategy for `sync()` — caller override or the id-union default. */
  const mergeForSync = mergeOnSync ?? unionById;

  /**
   * Optimistic single-record write to the BFF (when enabled), guarded by E1
   * concurrency control. Sends `If-Match` from {@link recordEtags} (or
   * create-only when the record was never pulled this session). On a 412
   * (another writer changed this record first) it re-pulls the winning copy,
   * hands it to `onConflict` so the store can adopt it into memory + cache, and
   * surfaces a toast — a record is opaque, so we don't guess a field merge.
   * `onConflict` receives `null` when the record was concurrently *deleted*.
   * Fire-and-forget: a transient (non-412) failure is swallowed — the IDB cache
   * holds the value and the next full sync reconciles.
   */
  const putRecordToBff = (
    record: T,
    onConflict?: (winner: T | null) => void,
  ): void => {
    if (!bffEnabled()) return;
    const path = recordPath(record.workshopId, record.id);
    const seen = recordEtags.get(path); // undefined = never pulled this session
    const precondition =
      typeof seen === "string"
        ? { ifMatch: seen }
        : seen === undefined
          ? { createOnly: true }
          : {}; // null → backend exposed no etag; blind write
    void putJsonWithEtag(WORKSHOPS_TIER, path, record, precondition)
      .then(({ etag }) => {
        recordEtags.set(path, etag);
      })
      .catch(async (err) => {
        if (!(err instanceof DocsConflictError)) return; // transient — swallow
        // Lost-update collision on this record. Re-pull the winner and adopt it.
        try {
          const { value, etag } = await getJsonWithEtag<Record<string, unknown>>(
            WORKSHOPS_TIER,
            path,
          );
          recordEtags.set(path, etag);
          // `WorkshopRecord.workshopId` is a plain string on the base type;
          // records are only ever minted with real workshop ids, so narrow it
          // for the sanitiser's fallback parameter.
          const winner = value
            ? sanitiseItem(value, record.workshopId as Workshop["id"])
            : null;
          onConflict?.(winner);
          toast.warning(
            "This record was changed in another session — reloaded the latest. Re-apply your change if needed.",
          );
        } catch {
          /* couldn't re-pull — leave it to the next full sync */
        }
      });
  };

  /** Fire-and-forget single-record delete from the BFF (when enabled).
   *  Deleting requires the `delete` grant (facilitator); for other roles the
   *  BFF returns 403 and the record reappears on the next full hydrate — the
   *  UI gates the control by role. */
  const deleteRecordFromBff = (workshopId: Workshop["id"], id: string): void => {
    if (!bffEnabled()) return;
    const path = recordPath(workshopId, id);
    recordEtags.delete(path); // gone — a re-create is a fresh create-only write
    void deleteDoc(WORKSHOPS_TIER, path).catch(() => {
      /* swallow — optimistic UI */
    });
  };

  return create<WorkshopRecordStore<T, P>>((set, get) => {
    /**
     * Adopt the winning copy of a record after a same-record 412 (E1): replace
     * the in-memory + cached record `id` in `workshopId`'s bucket with `winner`,
     * or drop it when `winner` is `null` (concurrently deleted). Called from the
     * `putRecordToBff` conflict path so this tab converges on the shared truth.
     */
    const adoptWinner = (
      workshopId: Workshop["id"],
      id: string,
      winner: T | null,
    ): void => {
      const current = get().entriesByWorkshop;
      const list = current[workshopId] ?? [];
      const nextList = winner
        ? list.some((r) => r.id === id)
          ? list.map((r) => (r.id === id ? winner : r))
          : [...list, winner]
        : list.filter((r) => r.id !== id);
      const next: EntriesByWorkshop<T> = { ...current, [workshopId]: nextList };
      set({ entriesByWorkshop: next });
      void cacheToIdb(next);
    };

    return {
      // Empty string when no BFF backend is wired — `recordType` only routes
      // E2 changed-paths, which never fire in IDB-only mode.
      recordType: bffEnabled() ? recordType : "",
      entriesByWorkshop: emptyEntries<T>(),
      isHydrated: false,
      loadError: null,
    // Sync slice — "disabled" until a BFF backend is configured (matches the
    // polling layer, which is inert without one, and keeps the header pill
    // hidden in the IDB-only dev/offline posture).
    syncState: bffEnabled() ? "idle" : "disabled",
    lastSyncedAt: null,
    syncError: null,
    /**
     * Force a re-pull from the BFF and merge it into memory. Driven by the
     * `SYNCED_STORES` polling loop (15 s / focus / cross-tab) for registered
     * stores; also the header pill's manual-sync target. Unlike `hydrate`
     * it isn't one-shot — it always re-fetches. No-op (disabled) without a
     * BFF backend. Never throws: `loadAllFromBff` degrades to the cache.
     */
    sync: async () => {
      if (!bffEnabled()) {
        if (get().syncState !== "disabled") set({ syncState: "disabled" });
        return;
      }
      set({ syncState: "syncing", syncError: null });
      const local = get().entriesByWorkshop;
      const { entries: fresh, failures } = await loadAllFromBff(local);
      if (failures === WORKSHOP_IDS.length) {
        set({
          syncState: "error",
          syncError: "Couldn’t reach the records service.",
        });
        return;
      }
      const merged = mergeForSync(fresh, local);
      set({
        entriesByWorkshop: merged,
        isHydrated: true,
        syncState: "synced",
        lastSyncedAt: Date.now(),
        syncError: null,
      });
      void cacheToIdb(merged);
    },
    hydrate: async () => {
      if (get().isHydrated) return;
      // Read the local cache first so an offline/unauthenticated reload still
      // shows what we had, and so a BFF outage degrades gracefully.
      let cached: unknown;
      try {
        cached = await idbGet(idbKey);
      } catch {
        cached = undefined;
      }
      const cachedEntries = sanitise(cached);
      if (bffEnabled()) {
        // Per-workshop fallback to the cache means a total outage yields the
        // full cache here; loadAllFromBff never throws. `failures` lets us
        // surface a "couldn't reach the server" signal while still showing it.
        const { entries: fresh, failures } = await loadAllFromBff(cachedEntries);
        // Merge the BFF snapshot (authoritative) over local records it doesn't
        // have yet: optimistic in-flight writes AND records still only in the
        // IDB cache (captured while the backend was IDB-only). Without this an
        // empty BFF on cutover would wipe unmigrated local data.
        const local = unionById(cachedEntries, get().entriesByWorkshop);
        const merged = unionById(fresh, local);
        const loadError =
          failures === WORKSHOP_IDS.length
            ? "Couldn’t reach the records service — showing your local copy."
            : null;
        set({ entriesByWorkshop: merged, isHydrated: true, loadError });
        void cacheToIdb(merged);
        return;
      }
      set({ entriesByWorkshop: cachedEntries, isHydrated: true, loadError: null });
    },
    add: (workshopId, record) => {
      const current = get().entriesByWorkshop;
      const list = current[workshopId] ?? [];
      const next: EntriesByWorkshop<T> = {
        ...current,
        [workshopId]: [...list, record],
      };
      set({ entriesByWorkshop: next });
      void cacheToIdb(next);
      putRecordToBff(record, (winner) => adoptWinner(workshopId, record.id, winner));
    },
    update: (workshopId, id, patch) => {
      const current = get().entriesByWorkshop;
      const list = current[workshopId] ?? [];
      let updated: T | undefined;
      const nextList = list.map((r) => {
        if (r.id !== id) return r;
        updated = { ...r, ...patch };
        return updated;
      });
      const next: EntriesByWorkshop<T> = { ...current, [workshopId]: nextList };
      set({ entriesByWorkshop: next });
      void cacheToIdb(next);
      if (updated) putRecordToBff(updated, (winner) => adoptWinner(workshopId, id, winner));
    },
    remove: (workshopId, id) => {
      const current = get().entriesByWorkshop;
      const list = current[workshopId] ?? [];
      const next: EntriesByWorkshop<T> = {
        ...current,
        [workshopId]: list.filter((r) => r.id !== id),
      };
      set({ entriesByWorkshop: next });
      void cacheToIdb(next);
      deleteRecordFromBff(workshopId, id);
    },
    pushLocalToBff: async () => {
      if (!bffEnabled()) return 0;
      // Read the DURABLE IDB cache, not in-memory state: the records captured
      // while the backend was IDB-only may never have been surfaced into
      // memory (a fresh, empty BFF returns nothing on hydrate), and the user
      // may click "upload" before hydrate even runs. The cache is the source
      // of truth for what needs migrating.
      let cached: unknown;
      try {
        cached = await idbGet(idbKey);
      } catch {
        cached = undefined;
      }
      const entries = sanitise(cached);
      const pending: Array<{ wsId: Workshop["id"]; record: T }> = [];
      for (const id of WORKSHOP_IDS) {
        for (const record of entries[id] ?? []) pending.push({ wsId: id, record });
      }
      await Promise.all(
        pending.map(({ wsId, record }) => {
          const path = recordPath(wsId, record.id);
          // Create-only: the migration must not clobber a record that already
          // exists on the BFF (e.g. written from another device since cutover).
          // A 412 means "already there" — that's success for a migration, not a
          // failure, so swallow it like any other.
          return putJsonWithEtag(WORKSHOPS_TIER, path, record, { createOnly: true })
            .then(({ etag }) => {
              recordEtags.set(path, etag);
            })
            .catch(() => {
              /* already present / transient — retried on the next run */
            });
        }),
      );
      return pending.length;
    },
    ingestRemoteChanges: async (changedPaths) => {
      if (!bffEnabled()) return 0;
      // Keep only paths that belong to this store's record type.
      const mine = changedPaths
        .map((path) => {
          const m = matchRecordPath(path);
          return m ? { path, wsId: m.wsId } : null;
        })
        .filter((x): x is { path: string; wsId: Workshop["id"] } => x !== null);
      if (mine.length === 0) return 0;

      // GET just the changed blobs (capturing etags for the E1 write path).
      // A single unreadable record is skipped, never fatal.
      const fetched = await Promise.all(
        mine.map(async ({ path, wsId }) => {
          try {
            const { value, etag } = await getJsonWithEtag<Record<string, unknown>>(
              WORKSHOPS_TIER,
              path,
            );
            recordEtags.set(path, etag);
            const rec = value ? sanitiseItem(value, wsId) : null;
            return rec ? { wsId, rec } : null;
          } catch {
            return null;
          }
        }),
      );
      const valid = fetched.filter(
        (x): x is { wsId: Workshop["id"]; rec: T } => x !== null,
      );
      if (valid.length === 0) return 0;

      // Build a fresh-bucket map of just the changed records, then merge it over
      // the current state with the same id-union/recency policy `sync()` uses —
      // so a just-saved local edit isn't clobbered by the polled change, and an
      // unchanged record is untouched (it isn't in `fresh`, and unionById/the
      // recency merge keep local extras).
      const fresh = emptyEntries<T>();
      for (const { wsId, rec } of valid) fresh[wsId] = [...fresh[wsId], rec];
      const merged = mergeForSync(fresh, get().entriesByWorkshop);
      set({ entriesByWorkshop: merged });
      void cacheToIdb(merged);
      return valid.length;
    },
    };
  });
}
